/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Typography} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import cloneDeep from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import isUndefined from 'lodash.isundefined';
import {resolveDeviceName} from '@ion/core/src/entities/Devices';
import {getEntityIds} from '@ion/core/src/entities/EntityUtils';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadDevicesBySmartSearch} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  CreateJob,
  Device,
  Devices,
  DownloadJob,
  DownloadJobTasks,
  DownloadJobType,
  Entity,
  EntityStatus,
  JobConflictResolution,
  PlatformIdTypes
} from '@ion/core/src/types/entities';
import {
  HierLevel,
  SearchCriteria,
  ServiceSearchParams,
  SmartSearch
} from '@ion/core/src/types/services';
import {getArrayDuplicates} from '@ion/core/src/utils/ArrayUtil';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {
  idMap,
  removeDuplicate as removeDuplicates
} from '@ion/core/src/utils/ObjectUtil';
import {isEmpty as isStringEmpty} from '@ion/core/src/utils/StringUtil';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import styles, {getClasses} from '@ion/dls/src/editors/editorStyles';
import {GroupChooser} from '@ion/dls/src/editors/GroupChooser';
import {SelectedEntityChips} from '@ion/dls/src/editors/SelectedEntityChips';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {SelectAllSearch} from '@ion/features/src/search/types';
import usePlatforms from '@ion/query/src/hooks/graphql/usePlatforms';
import {useDeviceAppIds} from '@ion/query/src/hooks/useDeviceAppIds';
import {useDeviceTypes} from '@ion/query/src/hooks/useDeviceTypes';
import {useLoadGroups} from '@ion/query/src/hooks/useLoadGroups';
import {DevicePlatformQueryParams} from '@ion/services/src/graphql/GraphQL';
import {getDeviceAppIds, getDeviceVbusIds} from '../../bulk/CreateJobDetailsForm';
import CreateJobDetailsFormError from '../../bulk/CreateJobDetailsFormError';
import {useCurrentGlobalGroup} from '../../hooks/useCurrentGlobalGroup';
import {useSanitizeAccount} from '../../hooks/useSanitizeAccount';
import {JobSearchEngine} from '../../pages/devices/DeviceActionDialogs';
import {
  clearDeviceSelection,
  DEFAULT_SMART_SEARCH_QUERY
} from '../../pages/devices/state';
import {devicesGridSearchSelector} from '../../pages/devices/state-selectors';
import {SearchFields} from '../../pages/devices/types';
import {errorOptions} from '../../prompts/constants';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import {
  BATCH_SIZE,
  cloneJobInfo,
  getAncestorIds,
  getIds,
  getJobErrorText,
  getJobQueryString,
  getMaxSearchWithIds,
  getPlatformIds,
  getSearchQueryString,
  getSelectedFilters,
  includesSpecificDevices,
  includeSubAccountDevices,
  normalizeAppIds,
  normalizeJobTasks
} from './helpers';
import JobConflictResolutionChooser from './JobConflictResolutionChooser';
import JobConflictsLoader from './JobConflictsLoader';
import {JobSelectDevicesDialog} from './JobSelectDevicesDialog';
import JobStepMarker from './JobStepMarker';
import {JobDeviceFileTypeChooserPermissionsProps} from './JobTaskDeviceFileTypeChooser';
import JobTasksView from './JobTasksView';
import {addJob} from './state';
import {useFilePermissionsJobCreation} from './useFilePermissionsJobCreation';
import useGetJobConflicts from './useGetJobConflicts';
import useJobConflictResolver from './useJobConflictResolver';
import {useJobCreate} from './useJobCreate';
import {useJobCreateMaxExceeded} from './useJobCreateMaxExceeded';
import {useJobCreateState} from './useJobCreateState';

export interface JobCreateEditorProps extends
  JobDeviceFileTypeChooserPermissionsProps,
  SelectAllSearch,
  JobSearchEngine {
  onCloseDialog?: () => void;
  selectedDevices?: Devices;
  selectedJob?: DownloadJob;
  includeSubAccounts?: boolean;
  disableAccountChooser?: boolean;
  jobSearchEngineEnabled?: boolean;
}

const styleOverrides = (theme: DLSTheme) => ({
  formPaper : {
    width                        : '100%',
    maxWidth                     : '1160px',
    [theme.breakpoints.up('lg')] : {
      maxWidth : '1360px'
    },
    [theme.breakpoints.down('sm')] : {
      maxWidth : '100%'
    }
  },
  formDialogContent : {
    maxWidth                     : 'calc(100% - 165px)',
    margin                       : 'auto',
    [theme.breakpoints.up('md')] : {
      maxWidth : 'calc(100% - 20%)'
    },
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    }
  },
  chipContainer : {
    display   : 'flex',
    flexWrap  : 'wrap',
    maxHeight : '80px',
    overflowY : 'auto'
  },
  chipWrapper : {
    marginBottom : '10px',
    position     : 'relative'
  },
  jobContent : {
    color : dlsColor('aaBlue')
  },
  filtersError : {
    ...dlsAlt('typography', 'label2'),
    letterSpacing : '0px',
    color         : dlsColor('caRed'),
    marginBottom  : '5px'
  },
  submit : {
    minWidth : '160px'
  },
  back : {
    display    : 'flex',
    alignItems : 'center',
    gap        : 4,
    ...dlsAlt('typography', 'label1'),
    color      : dlsColor('grey2'),
    cursor     : 'pointer'
  },
  step : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '10px'
  },
  previous : {
    marginLeft : '24px !important'
  },
  dialogTitle : {
    position : 'relative'
  }
});

const MAX_DEVICES_DISPLAY          = 100;
const MAX_DEVICE_NAMES_IN_JOB_NAME = 2;
const MAX_TASK_NAMES_IN_JOB_NAME   = 1;

/**
 * The Job Create editor is a shared component to manage the selection of data for and the
 * creation of a new Job to be run.
 *
 * It can be called with a set of specific job data (device selection, filters, smart-search
 * params, etc.) which will be used to create a new job, or it can be called with an existing
 * job record from which such data will be extracted (if the job is to be cloned, for example).
 *
 * ```typescript
 *
 *   import {JobCreateEditor} from '@ion/applications/src/components/jobs';
 *   ...
 *   <JobCreateEditor onCloseDialog={onCloseDialog}
 *     selectedDevices={selectedDevices}
 *     areTotalRowsSelected={areTotalRowsSelected}
 *     totalRowsCount={totalRowsCount}
 *     filters={filters}
 *     search={search} />
 *
 *   -OR-
 *
 *   <JobCreateEditor onCloseDialog={onCloseDialog}
 *     selectedJob={selectedJob} />
 * ```
 * @param {Function} onCloseDialog: callback to be fired when the dialog requests to be closed
 * @param {Devices} selectedDevices: selected devices for which the job will be created
 * @param {boolean} areTotalRowsSelected: indicates if all devices in the current account are
 * to be included in the job
 * @param {number} totalRowsCount: count of selected devices if all rows are selected
 * @param {FilterChips} filters: selected filters (if any) to determine the set of devices for
 * the job
 * @param {Search|SearchCriteria} search: smart-search criteria (if any) to determine the set of
 * devices for the job
 * @param {DownloadJob} selectedJob: existing job from which new job data can be determined (ex.
 * cloning the existing job)
 * @param {boolean} disableAccountChooser: indicates if the account for the job can be manipulated
 */
export const JobCreateEditor = ({
  Features,
  onCloseDialog,
  selectedDevices      : originalSelectedDevices,
  areTotalRowsSelected : originalAreTotalRowsSelected,
  totalRowsCount       : originalTotalRowsCount,
  filters              : originalFilters,
  search               : originalSearch,
  selectedJob          : originalSelectedJob,
  includeSubAccounts   : originalIncludeSubAccounts = false,
  disableAccountChooser = true,
  jobSearchEngineEnabled = false
}: JobCreateEditorProps) => {
  const dispatch                                = useDispatch();
  const dialogStyles                            = useExtendStyles(styles, styleOverrides);
  const classes                                 = dialogStyles();
  const [conflictsChecked, setConflictsChecked] = useState(false);
  const [selectedOptions, setSelectedOptions]   = useState({});
  const [conflicts, setConflicts]               = useState([]);
  const [activeStep, setActiveStep]             = useState(0);
  const devicesGridSearch                       = useSelector(devicesGridSearchSelector);

  const handleNext = function() {
    if (activeStep < conflicts.length - 1) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = function() {
    if (activeStep > 0) {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const {
    jobCreateLoading,
    jobCreateShowAddDevices: enableAddDevices = false
  } = useJobCreate();

  const {
    selectedDevices,
    setSelectedDevices,
    totalSelectedDevicesCount,
    setTotalSelectedDevicesCount,
    areTotalRowsSelected,
    setAreTotalRowsSelected,
    totalRowsCount,
    setTotalRowsCount,
    filters,
    setFilters,
    search,
    setSearch,
    includeSubAccounts,
    setIncludeSubAccounts,
    viewSelectDevices,
    setViewSelectDevices,
    clonedJob,
    setClonedJob,
    selectedChips,
    setSelectedChips,
    tasks,
    setTasks,
    tasksInitialized,
    setTasksInitialized,
    jobName,
    setJobName,
    jobNameUpdateEnabled,
    setJobNameUpdateEnabled,
    jobDescription,
    setJobDescription,
    isDevicesLoading,
    setDevicesLoading,
    isJobNameEmpty,
    setIsJobNameEmpty,
    isFileNameMissing,
    setIsFileNameMissing,
    isDuplicateFile,
    setIsDuplicateFile,
    isTaskGridEmpty,
    setIsTaskGridEmpty,
    isJobNameManuallyModified,
    setJobNameManuallyModified,
    setIsMaxDeviceSelected,
    isMaxDeviceSelected,
    setFilteredDeviceFileTypes,
    filteredDeviceFileTypes
  } = useJobCreateState({
    selectedDevices      : originalSelectedDevices,
    areTotalRowsSelected : originalAreTotalRowsSelected,
    totalRowsCount       : originalTotalRowsCount,
    filters              : originalFilters,
    search               : originalSearch,
    includeSubAccounts   : originalIncludeSubAccounts
  });

  const globalGroup = useCurrentGlobalGroup();
  const account     = useSanitizeAccount(
    originalSelectedJob?.account ||
    (originalSelectedDevices?.length === 1 && originalSelectedDevices?.[0]?.account) ||
    globalGroup
  );

  const accountId                  = entityId(account);
  const {deviceTypes}              = useDeviceTypes();
  const {groups: groupsRaw}        = useLoadGroups({accountId});
  const isSingleDevice             = originalSelectedDevices?.length === 1;
  const groups                     = useMemo(() => groupsRaw ? idMap(groupsRaw) : null, [groupsRaw]);
  const shouldBypassConflictsCheck = !isEmpty(originalFilters) && areTotalRowsSelected;

  const {
    maxLength,
    isMaxExceeded
  } = useJobCreateMaxExceeded({
    allRowsSelected : areTotalRowsSelected,
    rowCount        : totalRowsCount,
    selectedCount   : totalSelectedDevicesCount
  });

  const getPayload = (type, returnPlatformId = false): DevicePlatformQueryParams => {
    if (originalSelectedDevices?.length || selectedDevices?.length) {
      return {
        search : {
          filter : {
            accountId,
            ...(includeSubAccounts && {hierLevel: 'ALL'}),
            deviceId       : getIds(isSingleDevice ? originalSelectedDevices : selectedDevices),
            platformTypeId : type
          }
        },
        fields : [returnPlatformId ? 'platformId' : 'platformName']
      };
    } else if (areTotalRowsSelected) {
      return {
        search : {
          filter : {
            accountId,
            ...(includeSubAccounts && {hierLevel: 'ALL'}),
            platformTypeId : type
          }
        },
        fields : [returnPlatformId ? 'platformId' : 'platformName']
      };
    }
  };
  
  const enablePlatformApis = useMemo(() => (!!selectedDevices?.length ||
    (!originalFilters?.length && areTotalRowsSelected)),
  [
    selectedDevices,
    originalFilters,
    areTotalRowsSelected
  ]);

  const {
    platforms: fileTypes,
    errorResult,
    resetError,
    isFetching,
    isError,
    isFetched
  } = usePlatforms(getPayload(PlatformIdTypes.FILES, true), enablePlatformApis);

  const {
    platforms: devicesAppIds
  } = usePlatforms(getPayload(PlatformIdTypes.APP_IDS), enablePlatformApis && !isSingleDevice);

  const {
    platforms: vbusIds
  } = usePlatforms(getPayload(PlatformIdTypes.VBUS, true), enablePlatformApis && !isSingleDevice);

  const {
    customQuery,
    queryString,
    usingSelectedDevicesAppIds
  } = getDeviceAppIdsParams({
    originalFilters,
    customQuery : devicesGridSearch,
    areTotalRowsSelected,
    totalRowsCount,
    totalSelectedDevicesCount,
    search
  });

  let {deviceAppIds: appIds} = useDeviceAppIds({
    customQuery,
    accountId,
    queryString,
    includeSubAccounts
  });

  if (usingSelectedDevicesAppIds) {
    appIds = [];
    selectedDevices.forEach(({extensions}) => {
      const appId = extensions?.appId;
      if (appId) {
        appIds.push(String(appId));
      }
    });
  };

  const deviceAppIds = useMemo(() => normalizeAppIds(appIds), [safeJSONStringify(appIds)]);
  const platformIds  = getPlatformIds(selectedDevices);

  const titleTranslation  = translate('jobcreate:title');
  const cautionTitle      = translate('jobcreate:caution');
  const fieldTranslations = translate('jobcreate:fields', {returnObjects: true});

  useFilePermissionsJobCreation({
    Features,
    deviceTypes,
    isFetching,
    enablePlatformApis,
    deviceFileTypes : fileTypes,
    setFilteredDeviceFileTypes
  });

  const {
    getDeviceConflicts,
    isConflictsLoading,
    devicesInConflict,
    setShowConflicts,
    showConflicts
  } = useGetJobConflicts({
    accountId,
    devices             : selectedDevices,
    searchEntireAccount : areTotalRowsSelected,
    tasks
  });

  const {
    cancelSelectedConflicts,
    isCanceling
  } = useJobConflictResolver({
    selectedOptions,
    accountId,
    conflicts,
    devices : areTotalRowsSelected ? devicesInConflict : selectedDevices
  });

  const areAllConflictsResolved = useMemo(() => {
    return conflicts?.every((conflictGroup) =>
      conflictGroup?.every((conflict) => {
        const conflictKey = `${conflict?.id}-${conflict?.task}`;
        return selectedOptions[conflictKey] !== undefined;
      })
    );
  }, [conflicts, selectedOptions]);

  const areAllConflictsSelectedForStep = useMemo(() => {
    const currentConflicts = conflicts?.[activeStep];
    if (!currentConflicts) return false;
    return currentConflicts?.every((conflict) => {
      const conflictKey = `${conflict?.id}-${conflict?.task}`;
      return selectedOptions[conflictKey] !== undefined;
    });
  }, [conflicts, selectedOptions, activeStep]);

  useEffect(() => {
    if (originalSelectedDevices) {
      const devices: Device[] = cloneDeep(originalSelectedDevices).map((device: Device) => {
        device.name = resolveDeviceName(device);
        return device;
      });
      setSelectedDevices(devices);
      setTotalSelectedDevicesCount(devices.length);
      setTasksInitialized(true);
      setJobNameUpdateEnabled(true);
    }
  }, [originalSelectedDevices]);

  useEffect(() => {
    if (originalSelectedJob) {
      cloneSelectedJob({
        selectedJob : originalSelectedJob,
        setClonedJob,
        dispatch,
        onCloseDialog
      });
    }
  }, [originalSelectedJob]);

  useEffect(() => {
    if (clonedJob && groups) {
      const {name, description, tasks, criteria} = clonedJob;
      const customQuery          = criteria?.[0]?.smartSearch?.query;
      const queryString          =  !customQuery && getJobQueryString(clonedJob);
      const areTotalRowsSelected = !!customQuery && queryString && !includesSpecificDevices(queryString);
      const includeSubAccounts   = includeSubAccountDevices(clonedJob);
      getSelectedDevicesAndFilters({
        queryString,
        customQuery,
        areTotalRowsSelected,
        includeSubAccounts,
        groups,
        accountId,
        setDevicesLoading,
        setSelectedDevices,
        setTotalSelectedDevicesCount,
        setAreTotalRowsSelected,
        setTotalRowsCount,
        setFilters,
        setSearch,
        clonedJob
      });
      setIncludeSubAccounts(includeSubAccounts);
      setJobName(name);
      setJobDescription(description);
      setTasks({tasks: getFileTasks(tasks)});
      setTasksInitialized(true);
    }
  }, [clonedJob, groups]);

  useEffect(() => {
    if (totalSelectedDevicesCount > MAX_DEVICES_DISPLAY) {
      setSelectedChips(tooManyDevicesSelected(totalSelectedDevicesCount));
    } else {
      setSelectedChips(selectedDevices);
    }
  }, [selectedDevices, totalSelectedDevicesCount]);

  useEffect(() => {
    if (jobNameUpdateEnabled && !isJobNameManuallyModified) {
      const jobTasks  = tasks?.tasks;
      const firstTask = jobTasks?.[0]?.task || {};
      const {
        deviceType = '',
        fileType = '',
        fileNameUI = ''
      } = firstTask;
      const taskCount = jobTasks?.length || 0;
      let deviceCount, devicesLabel, tasksLabel, preposition;
      if (areTotalRowsSelected) {
        deviceCount = totalRowsCount;
      } else if (totalSelectedDevicesCount > MAX_DEVICES_DISPLAY) {
        deviceCount = totalSelectedDevicesCount;
      } else {
        deviceCount = selectedDevices.length;
      }
      if (!areTotalRowsSelected && (deviceCount <= MAX_DEVICE_NAMES_IN_JOB_NAME)) {
        devicesLabel = `${selectedDevices.map(({name, id}) => name || id).join('/')}`.trim();
      } else {
        devicesLabel = `${deviceCount} ${translate('common:entityLabels.devices')}`;
      }
      if (taskCount <= MAX_TASK_NAMES_IN_JOB_NAME) {
        tasksLabel = `${deviceType} ${fileType} ${fileNameUI}`.trim();
      } else {
        tasksLabel = `${taskCount} ${translate('common:entityLabels.files')}`;
      }
      if (devicesLabel.length && tasksLabel.length) {
        preposition = translate('common:prepositions.with');
      } else {
        preposition = '';
      }
      setJobName(`${devicesLabel} ${preposition} ${tasksLabel}`.trim());
    }
  }, [
    selectedDevices,
    totalSelectedDevicesCount,
    areTotalRowsSelected,
    totalRowsCount,
    tasks,
    jobNameUpdateEnabled,
    isJobNameManuallyModified
  ]);

  useEffect(() => {
    if (jobName?.length) {
      setIsJobNameEmpty(false);
    }
  }, [jobName]);

  const onAddDevices = useCallback(function(show: boolean = true) {
    dispatch(clearDeviceSelection());
    setViewSelectDevices(show);
  }, []);

  const onSelectDeviceDialogClose = useCallback(function() {
    setViewSelectDevices(false);
  }, []);

  const onChipDelete = useCallback(function(selectedItem) {
    const filteredItems = selectedChips?.filter((item) => {
      return item.id !== selectedItem.id;
    });
    setSelectedDevices(filteredItems);
    setTotalSelectedDevicesCount(filteredItems.length);
  }, [selectedChips]);

  const onUpdateSelectedDevices = function({
    selectedDevices      : newSelectedDevices,
    includeSubAccounts   : newIncludeSubAccounts,
    areTotalRowsSelected : newAreTotalRowsSelected,
    totalRowsCount       : newTotalRowsCount,
    search               : newSearch,
    gridSearch           : newGridSearch,
    filters              : newFilters
  }) {
    const customQuery = newGridSearch?.query;
    const queryString = !customQuery && getSearchQueryString(newSearch);
    if (newAreTotalRowsSelected || newSelectedDevices?.length) {
      if (newAreTotalRowsSelected) {
        getSelectedDevicesAndFilters({
          queryString,
          customQuery,
          areTotalRowsSelected : newAreTotalRowsSelected,
          includeSubAccounts   : newIncludeSubAccounts,
          clonedJob            : null,
          groups,
          accountId,
          setDevicesLoading,
          setSelectedDevices,
          setTotalSelectedDevicesCount,
          setAreTotalRowsSelected,
          setTotalRowsCount,
          setFilters,
          setSearch
        });
      } else {
        const resolvedDevices = resolveDeviceNames(cloneDeep(newSelectedDevices));
        const uniqueDevices   = removeDuplicates(
          'id',
          [...selectedDevices, ...resolvedDevices]
        );
        setSelectedDevices(uniqueDevices);
        setTotalSelectedDevicesCount(uniqueDevices.length);
        setTotalRowsCount(0);
        setFilters([]);
        setSearch({} as SearchCriteria);
        setAreTotalRowsSelected(newAreTotalRowsSelected);
      }
      setIncludeSubAccounts(newIncludeSubAccounts);
      setJobNameUpdateEnabled(true);
    }
  };

  const onChangeJobName = useCallback(function({target}) {
    const name = target.value;
    setJobName(name);
    setJobNameManuallyModified(true);
  }, []);

  const onChangeJobDescription = useCallback(function({target}) {
    const description = target.value;
    setJobDescription(description);
  }, []);

  const onUpdateTasks = useCallback(function(tasks: CreateJob) {
    let isValid: boolean = true;
    if (!isNil(tasks)) {
      const {requiredField, fileNames} = checkTasks(tasks);
      if (requiredField) {
        isValid = false;
      }
      !requiredField && setIsFileNameMissing(requiredField);

      const duplicates  = getArrayDuplicates(fileNames);
      const isDuplicate = !!duplicates.length;
      if (isDuplicate) {
        isValid = false;
      }
      setIsDuplicateFile(isDuplicate);
    } else {
      isValid = false;
    }
    return isValid;
  }, []);

  const resetConflictState = useCallback(() => {
    setConflictsChecked(false);
    setSelectedOptions({});
    setShowConflicts(false);
    setActiveStep(0);
  }, []);

  const validateForm = useCallback((taskForm) => {
    const existingTasks: DownloadJobTasks = taskForm?.tasks;
    const isTaskGridEmpty                 = existingTasks?.length === 0;
    const isJobNameEmpty                  = isStringEmpty(jobName);
    const {requiredField}                 = checkTasks(taskForm);
  
    setIsTaskGridEmpty(isTaskGridEmpty);
    setIsJobNameEmpty(isJobNameEmpty);
    setIsFileNameMissing(requiredField);
  
    const isValid = !(
      isTaskGridEmpty ||
      isJobNameEmpty ||
      !onUpdateTasks(taskForm) ||
      requiredField ||
      (isMaxExceeded && setIsMaxDeviceSelected(true))
    );
  
    return {
      isValid,
      existingTasks
    };
  }, [jobName, isMaxExceeded]);

  const handleSubmit = async function(_tasks, jobName, jobDescription) {
    const {isValid, existingTasks} = validateForm(_tasks);
    if (!isValid) {
      return;
    }

    let finalDevices = [...selectedDevices];

    if (jobSearchEngineEnabled && !shouldBypassConflictsCheck) {
      let devicesWithConflicts = [];
      if (!conflictsChecked) {
        devicesWithConflicts = await getDeviceConflicts();
        setConflictsChecked(true);
      }

      if (devicesWithConflicts.length > 0) {
        setConflicts(devicesWithConflicts);
        const areConflictsResolved = Object.keys(selectedOptions).length === devicesWithConflicts.length;
        if (!areConflictsResolved) {
          setShowConflicts(true);
          return;
        }
      }

      // After conflict detection, if any devices are marked to be removed from
      // a job, we update finalDevices to submit along with the job creation.
      finalDevices = selectedDevices?.filter(device => {
        const shouldRemove = conflicts?.some(conflictGroup => {
          return conflictGroup?.some(conflict => {
            const conflictKey = `${conflict?.id}-${conflict?.task}`;
            if (selectedOptions?.[conflictKey] !== JobConflictResolution.REMOVE) {
              return false;
            }
            const matchingDeviceFound = conflict?.devices?.some(conflictDevice => {
              return conflictDevice?.deviceIdentifier === device?.esn;
            });
            return matchingDeviceFound;
          });
        });
        return !shouldRemove;
      });

      setSelectedDevices(finalDevices);
      
      await cancelSelectedConflicts();
    }
    
    if (isValid) {
      const newJob = createJobRequest({
        name            : jobName,
        description     : jobDescription,
        tasks           : existingTasks,
        customQuery     : devicesGridSearch,
        account,
        accountId,
        selectedDevices : jobSearchEngineEnabled ? finalDevices : selectedDevices,
        areTotalRowsSelected,
        search,
        includeSubAccounts,
        clonedJob
      });
      const normalizedJob = normalizeJobTasks(newJob);
      try {
        await dispatch(addJob({job: normalizedJob}) as any);
      } catch (err) {
        setTasks({tasks: getFileTasks(existingTasks)});
        resetConflictState();
        error('Failed to create job:', err);
      }
    }
  };

  const somethingLoading: boolean = jobCreateLoading || isDevicesLoading;

  const onDismiss = useCallback(() => {
    setIsMaxDeviceSelected(false);
  }, []);

  const handleClose = useCallback(() => {
    if (showConflicts && jobSearchEngineEnabled) {
      resetConflictState();
    } else {
      onCloseDialog();
    }
  }, [showConflicts]);

  return (
    <Dialog open
      onBackdropClick={null}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}
      className={classes.dialogTitle}>
        <div className={classes.formTitle}>
          <div onClick={handleClose} className={classes.closeButton}>
            <ConditionalRender condition={!showConflicts}>
              <MaterialExitCloseIcon size={IconSize.medium}
                color={dlsColor('grey3')} />
            </ConditionalRender>
            <ConditionalRender condition={showConflicts}>
              <div className={classes.back}>
                <ChevronLeft />
                <span>Create Job</span>
              </div>
            </ConditionalRender>
          </div>
          <Typography component='h1' variant='h2'>
            <ConditionalRender condition={!showConflicts}>
              {titleTranslation}
            </ConditionalRender>
            <ConditionalRender condition={showConflicts}>
              {cautionTitle}
            </ConditionalRender>
          </Typography>
        </div>
      </DialogTitle>
      <form className={classes.form}>
        <div className={jobSearchEngineEnabled ? classes.formWrapperCtcAlt : classes.formWrapperCtc}>
          <ConditionalRender condition={isConflictsLoading}>
            <JobConflictsLoader />
          </ConditionalRender>
          <ConditionalRender condition={!isConflictsLoading}>
            <DialogContent className={classes.formDialogContent}>
              <ConditionalRender condition={showConflicts && jobSearchEngineEnabled}>
                <JobConflictResolutionChooser conflicts={conflicts}
                  setSelectedOptions={setSelectedOptions}
                  selectedDevices={selectedDevices}
                  selectedOptions={selectedOptions}
                  currentJobName={jobName}
                  activeStep={activeStep}
                  selectedTasks={tasks}/>
              </ConditionalRender>
              <ConditionalRender condition={!showConflicts}>
                <FormSection title={
                  <>
                    <ConditionalRender condition={jobSearchEngineEnabled}>
                      <div className={classes.step}>
                        <JobStepMarker step={1} />
                        <div>
                          {translate('jobcreate:selectedDevices')}
                        </div>
                      </div>
                    </ConditionalRender>
                    <ConditionalRender condition={!jobSearchEngineEnabled}>
                      {translate('jobcreate:selectedDevices')}
                    </ConditionalRender>
                  </>
                }
                legend={
                  <>
                    <ConditionalRender condition={jobSearchEngineEnabled && enableAddDevices}>
                      <PrimaryButton onClick={onAddDevices}>
                        {translate('jobcreate:addDevices')}
                      </PrimaryButton>
                    </ConditionalRender>
                    <ConditionalRender condition={!jobSearchEngineEnabled && enableAddDevices}>
                      <TertiaryButton onClick={onAddDevices}>
                        {translate('jobcreate:addDevices')}
                      </TertiaryButton>
                    </ConditionalRender>
                  </>
                }>
                  <FormItem fullWidth>
                    <ConditionalRender condition={jobSearchEngineEnabled && shouldBypassConflictsCheck}>
                      <div className={classes.filtersError}>
                        {translate('jobcreate:conflict.warning.check')}
                      </div>
                    </ConditionalRender>
                    <SelectedEntityChips noTextTransform={false}
                      selected={selectedChips}
                      filters={originalFilters}
                      accountName={account.title}
                      areTotalRowsSelected={areTotalRowsSelected}
                      includeSubAccounts={includeSubAccounts}
                      onDelete={onChipDelete} />
                  </FormItem>
                </FormSection>
                <JobTasksView tasks={tasks}
                  isFetching={isFetching}
                  showInfoMsg={areTotalRowsSelected && !!originalFilters?.length}
                  setTasks={setTasks}
                  onUpdateTasks={onUpdateTasks}
                  tasksInitialized={tasksInitialized}
                  deviceTypes={filteredDeviceFileTypes}
                  deviceAppIds={getDeviceAppIds(devicesAppIds, deviceAppIds, enablePlatformApis)}
                  accountId={accountId}
                  includeSubAccounts={includeSubAccounts}
                  setJobNameUpdateEnabled={setJobNameUpdateEnabled}
                  isFileNameMissing={isFileNameMissing}
                  isDuplicateFile={isDuplicateFile}
                  devicePlatformIds={getDeviceVbusIds(vbusIds, platformIds, enablePlatformApis)}
                  isDevicesSelected={!!selectedDevices.length || areTotalRowsSelected}
                  setIsTaskGridEmpty={setIsTaskGridEmpty}
                  isTaskGridEmpty={isTaskGridEmpty}
                  stepper={jobSearchEngineEnabled} />
                <FormSection title={' '}
                  legend={translate('common:editors.shared.fieldsRequired')}>
                  <FormItem>
                    <FormTextField required
                      fieldLabel={`${fieldTranslations.name.label}`}
                      placeholder={`${fieldTranslations.name.placeholder}`}
                      onChange={onChangeJobName}
                      hasError={isJobNameEmpty}
                      errorText={isJobNameEmpty ? translate('common:editors.shared.required') : ''}
                      value={jobName}
                      maxLength={64} />
                  </FormItem>
                  <FormItem>
                    <FormTextField fieldLabel={`${fieldTranslations?.description?.label}`}
                      placeholder={`${fieldTranslations?.description?.placeholder}`}
                      value={jobDescription}
                      onChange={onChangeJobDescription}
                      maxLength={120} />
                  </FormItem>
                  <FormItem>
                    <GroupChooser fieldLabel={`${fieldTranslations?.accountName?.label}`}
                      includeGroupProperties={false}
                      account={account}
                      readOnly={disableAccountChooser}
                      defaultValue={account.title} />
                  </FormItem>
                </FormSection>
              </ConditionalRender>
            </DialogContent>
          </ConditionalRender>
        </div>
      </form>
      <DialogActions className={classes.formActions}>
        <ConditionalRender condition={!isConflictsLoading}>
          <TertiaryButton onClick={onCloseDialog}>
            {translate('common:editors.shared.cancel')}
          </TertiaryButton>
        </ConditionalRender>

        <ConditionalRender condition={showConflicts && conflicts?.length > 1}>
          <TertiaryButton className={classes.previous}
            startIcon={<ChevronLeft />}
            onClick={handleBack}
            disabled={activeStep === 0}>
            {translate('jobcreate:back')}
          </TertiaryButton>
        </ConditionalRender>

        <ConditionalRender condition={showConflicts}>
          <ConditionalRender condition={activeStep < conflicts.length - 1}>
            <PrimaryButton endIcon={<ChevronRight />}
              disabled={!areAllConflictsSelectedForStep}
              onClick={handleNext}>
              {translate('jobcreate:next')}
            </PrimaryButton>
          </ConditionalRender>

          <ConditionalRender condition={activeStep >= conflicts.length - 1}>
            <PrimaryButton className={classes.submit}
              onClick={function() {
                handleSubmit(tasks, jobName, jobDescription);
              }}
              disabled={somethingLoading || isCanceling ||
            (areTotalRowsSelected && !totalRowsCount) ||
            (!areTotalRowsSelected && !totalSelectedDevicesCount) ||
            (showConflicts && !areAllConflictsResolved)}>
              {isConflictsLoading || somethingLoading ? <ButtonSpinner /> : translate('jobcreate:submit')}
            </PrimaryButton>
          </ConditionalRender>
        </ConditionalRender>

        <ConditionalRender condition={!showConflicts}>
          <PrimaryButton className={classes.submit}
            onClick={function() { handleSubmit(tasks, jobName, jobDescription); }}
            disabled={somethingLoading || isCanceling ||
            (areTotalRowsSelected && !totalRowsCount) ||
            (!areTotalRowsSelected && !totalSelectedDevicesCount)}>
            {isConflictsLoading || somethingLoading ? <ButtonSpinner /> : translate('jobcreate:submit')}
          </PrimaryButton>
        </ConditionalRender>
      </DialogActions>
      {viewSelectDevices && (
        <JobSelectDevicesDialog account={account}
          breadcrumbBackLabel={titleTranslation}
          onBreadcrumbBackClick={onSelectDeviceDialogClose}
          onCloseDialog={onSelectDeviceDialogClose}
          onSelectDevicesDone={onUpdateSelectedDevices} />
      )}
      {isMaxDeviceSelected && (
        <UserNotificationDialog show
          capitalize
          title={translate('common:maxDevices.information')}
          message={translate('common:maxDevices.identifierMessage', {maxDevices: maxLength})}
          dismissLabel={translate('common:ok')}
          onDismiss={onDismiss} />
      )}
      <CreateJobDetailsFormError isError={isError}
        isFetched={isFetched}
        errorResult={errorResult}
        resetError={resetError}
        fileTypes={fileTypes} />
    </Dialog>
  );
};

/**
 * Determines the parameters to be used for fetching app IDs for the chosen set of devices.
 * If all devices have been selected (areTotalRowsSelected is true), totalRowsCount contains
 * the selected device count; otherwise totalSelectedDeviceCount contains the individually
 * selected device count.
 *
 * If this count is over 1000, we want to get all app IDs in the account (using device search
 * criteria = DEFAULT_SMART_SEARCH_QUERY = 'id: *' to get the aggregated list of app IDs).
 * Otherwise:
 *   - If all devices have been selected, we want to use the device search criteria to get
 *     the aggregated list of app IDs for those devices.
 *   - If devices have been individually selected:
 *     - If the count is over 100 (MAX_DEVICES_DISPLAY = platform limit), we need to use the
 *       device search criteria (long list of individual device IDs) to get the aggregated
 *       list of app IDs (since we don't have all the individual devices).
 *     - Otherwise, we can just get the app IDs from the individual devices.
 */
const getDeviceAppIdsParams = ({
  areTotalRowsSelected,
  totalRowsCount,
  totalSelectedDevicesCount,
  search,
  originalFilters,
  customQuery : newCustomQuery
}) => {
  let customQuery                         = null;
  let queryString: string                 = DEFAULT_SMART_SEARCH_QUERY;
  let usingSelectedDevicesAppIds: boolean = false;

  if (areTotalRowsSelected) {
    if (typeof search?.smartSearch?.queryString !== 'boolean') {
      if (search?.smartSearch?.queryString?.includes(SearchFields.APP_ID)) {
        usingSelectedDevicesAppIds = false;
        queryString = getSearchQueryString(search as SmartSearch);
      } else if (totalRowsCount <= 1000) {
        queryString = getSearchQueryString(search as SmartSearch);
      }
    } else if (originalFilters?.length) {
      customQuery = newCustomQuery;
    }
  } else {
    if (totalSelectedDevicesCount <= 1000) {
      if (totalSelectedDevicesCount > MAX_DEVICES_DISPLAY) {
        queryString = getSearchQueryString(search as SmartSearch);
      } else {
        usingSelectedDevicesAppIds = true;
      }
    }
  }

  return {customQuery, queryString, usingSelectedDevicesAppIds};
};

/**
 * Clones and stores the specified job's information.
 */
const cloneSelectedJob = async({
  selectedJob,
  setClonedJob,
  dispatch,
  onCloseDialog
}) => {
  try {
    const clone = await cloneJobInfo(selectedJob);
    setClonedJob(clone);
  } catch (err) {
    dispatch(toggleUserPrompt({
      ...errorOptions,
      actionLabel   : translate('common:ok'),
      message       : getJobErrorText(err),
      translateText : false
    }));
    onCloseDialog?.();
    error(err);
  }
};

/**
 * Resolves names for all specified devices.
 */
const resolveDeviceNames = (devices: Devices): Devices => {
  return devices.map((device: Device) => {
    device.name = resolveDeviceName(device);
    return device;
  });
};

/**
 * Retrieves device and filter information associated with the specified query string,
 * to be displayed via chips.
 */
const getSelectedDevicesAndFilters = async({
  queryString,
  customQuery,
  areTotalRowsSelected,
  includeSubAccounts,
  groups,
  accountId,
  setDevicesLoading,
  setSelectedDevices,
  setTotalSelectedDevicesCount,
  setAreTotalRowsSelected,
  setTotalRowsCount,
  setFilters,
  setSearch,
  clonedJob
}) => {
  setDevicesLoading(true);
  let options = [];
  try {
    const loadAllDevices = async(pageId = 1) => {
      const {devices, totalCount} = await loadDevicesBySmartSearch({
        pageSize         : MAX_DEVICES_DISPLAY,
        usePulse         : true,
        includeSubGroups : includeSubAccounts,
        queryString,
        customQuery,
        accountId,
        pageId
      });
      options = [...options, ...devices];
      if ((totalCount / (pageId * 100)) > 1 && clonedJob?.name) {
        loadAllDevices(pageId + 1);
        setDevicesLoading(true);
      } else {
        setDevicesLoading(false);
        const resolvedDevices = resolveDeviceNames(options);
        const filters         = getSelectedFilters(queryString, resolvedDevices, groups);
        setFilters(filters);
        setSearch({
          smartSearch : {
            ...(includeSubAccounts && {hierLevel: HierLevel.ALL}),
            type : Entity.device,
            queryString,
            accountId
          }
        });
        if (areTotalRowsSelected) {
          setTotalRowsCount(totalCount);
          setSelectedDevices([]);
          setTotalSelectedDevicesCount(0);
        } else {
          setSelectedDevices(resolvedDevices);
          setTotalSelectedDevicesCount(totalCount);
        }
      }
      setAreTotalRowsSelected(areTotalRowsSelected);
    };
    await loadAllDevices();
  } catch (err) {
    error(err);
  }
};

/**
 * Returns an updated array of tasks with assigned Id, ancestorIds, and children values.
 */
const getTasksWithDependencies = (tasks: DownloadJobTasks) => {
  const jobTasksWithId = tasks?.map((task, index) => ({
    ...task,
    id          : `${index + 1}`,
    ancestorIds : [],
    task        : {
      ...task.task,
      children : []
    }
  }));

  return jobTasksWithId?.map((task) => {
    const dependencyIndex = task.task.metaData?.dependency?.index;
    if (!isUndefined(dependencyIndex)) {
      const parentTask = jobTasksWithId[dependencyIndex];
      if (parentTask) {
        parentTask.task.children.push(task.id);
        task.ancestorIds = getAncestorIds(jobTasksWithId, dependencyIndex);
      }
    }
    return task;
  });
};

/**
 * Retrieves all file-based tasks from the current task list.
 */
const getFileTasks = (tasks: DownloadJobTasks) => {
  const fileTasks = tasks?.filter(task => task?.task?.taskType === 'File');
  return getTasksWithDependencies(fileTasks);
};

/**
 * Retrieves task file information for validation.
 */
const checkTasks = (tasks: CreateJob) => {
  const requiredField = [];
  const fileNames     = [];
  let rows;
  tasks?.tasks?.forEach((data, index) => {
    rows = data?.task;
    if (!rows.fileName?.length) {
      requiredField.push(index);
    } else {
      fileNames.push(rows.metaData.file);
    }
  });
  return {requiredField: !!requiredField.length, fileNames};
};

/**
 * Creates a summary chip which will be displayed if more than 100 devices are selected
 * for the current job.
 */
const tooManyDevicesSelected = (count: number) => (
  [{id: -1, name: translate('jobcreate:tooManyDevices', {max: MAX_DEVICES_DISPLAY, count})}]
);

/**
 * Returns provided customQuery along with hierLevel, type and accountId properties if missing.
 */
const createSmartSearchParams = (
  customQuery: ServiceSearchParams | SmartSearch,
  includeSubAccounts: boolean,
  accountId: number
): ServiceSearchParams => {
  const includeHier    = (includeSubAccounts && !customQuery?.hierLevel);
  const includeEntity  = !customQuery?.type;
  const includeAccount = !customQuery?.accountId;
  return {
    ...customQuery,
    ...(includeHier && {hierLevel: HierLevel.ALL}),
    ...(includeEntity && {type: Entity.device}),
    ...(includeAccount && {accountId})
  };
};

/**
 * Creates a new job object to be sent to the platform.
 */
const createJobRequest = ({
  name,
  description,
  customQuery,
  tasks,
  account,
  accountId,
  selectedDevices,
  areTotalRowsSelected,
  search,
  includeSubAccounts,
  clonedJob
}): CreateJob => {
  let selectAllQuery = getSmartSearchWithIds(selectedDevices, accountId, includeSubAccounts);
  if (areTotalRowsSelected) {
    if (typeof search?.smartSearch?.queryString !== 'boolean') {
      selectAllQuery = search;
    } else {
      selectAllQuery = {
        smartSearch : createSmartSearchParams(customQuery, includeSubAccounts, accountId)
      };
    }
  }
  return {
    criteria : selectedDevices?.length > BATCH_SIZE && clonedJob?.name
      ? getMaxSearchWithIds(selectedDevices, accountId, includeSubAccounts) : [selectAllQuery],
    status         : EntityStatus.Enabled,
    progressStatus : DownloadJobType.Pending,
    account,
    name,
    description,
    tasks
  };
};

/**
 * Creates a smart search criteria object for a specified list of devices.
 */
const getSmartSearchWithIds = (devices: Devices, accountId: number, includeSubAccounts: boolean): SmartSearch => ({
  smartSearch : {
    queryString : `id: (${getEntityIds(devices).join(' OR ')})`,
    type        : Entity.device,
    ...(includeSubAccounts && {hierLevel: HierLevel.ALL}),
    accountId
  }
});

export default JobCreateEditor;
